import { MarkdownViewer } from '@/components/MarkdownViewer'
import { isUrl, tryParseJson } from '@/lib/utils'

// Markdown rendering collapses single newlines and reads a leading `#` or `*` as syntax, which would
// mangle any output that is not prose — so it is opt-in per key, not a fallback for every plain string.
const MARKDOWN_OUTPUT_KEYS = new Set(['result'])

export interface NodeOutputEntry {
  key: string
  value: string
}

export function hasCopyableContent(value: string): boolean {
  if (!value.trim()) return false

  const parsed = tryParseJson(value)
  if (Array.isArray(parsed) && parsed.length === 0) return false
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length === 0) return false

  return true
}

// The node-detail endpoint serves the stored OutputsJson column verbatim, so each value arrives wrapped
// as { Value, OutputType } rather than as the flat string the run-detail query projects.
export function parseOutputsJson(outputsJson: string | null): NodeOutputEntry[] | null {
  if (!outputsJson?.trim()) return []

  let parsed: unknown
  try {
    parsed = JSON.parse(outputsJson)
  } catch {
    return null
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  return Object.entries(parsed as Record<string, unknown>).map(([key, raw]) => ({
    key,
    value: unwrapOutputValue(raw),
  }))
}

function unwrapOutputValue(raw: unknown): string {
  if (typeof raw === 'string') return raw
  if (raw === null || raw === undefined) return ''

  if (typeof raw === 'object' && 'Value' in (raw as object)) {
    const inner = (raw as { Value: unknown }).Value
    return typeof inner === 'string' ? inner : JSON.stringify(inner, null, 2)
  }

  return JSON.stringify(raw, null, 2)
}

export function renderOutputValue(key: string, value: string) {
  const parsed = tryParseJson(value)

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) {
      return <p className="text-xs text-muted-foreground">Empty list.</p>
    }

    if (parsed.every(isUrl)) {
      return (
        <ul className="text-sm space-y-1 pr-10">
          {parsed.map((url, idx) => (
            <li key={idx}>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">{url}</a>
            </li>
          ))}
        </ul>
      )
    }

    if (parsed.every(item => item && typeof item === 'object' && 'repo' in (item as object) && 'reason' in (item as object))) {
      return (
        <ul className="text-sm space-y-1 pr-10">
          {(parsed as Array<{ repo: string; reason: string }>).map((item, idx) => (
            <li key={`${item.repo}-${idx}`} className="text-xs">
              <code className="bg-muted px-1.5 py-0.5 rounded">{item.repo}</code>
              <span className="text-muted-foreground"> — {item.reason}</span>
            </li>
          ))}
        </ul>
      )
    }
  }

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const entries = Object.entries(parsed as Record<string, unknown>)
    if (entries.length > 0 && entries.every(([, v]) => v && typeof v === 'object' && 'url' in (v as object))) {
      return (
        <ul className="text-sm space-y-1 pr-10">
          {entries.map(([label, val]) => {
            const obj = val as { url: string; number?: string }
            return (
              <li key={label} className="flex items-center gap-2">
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{label}</code>
                <a href={obj.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {obj.number ? `#${obj.number}` : obj.url}
                </a>
              </li>
            )
          })}
        </ul>
      )
    }
  }

  if (MARKDOWN_OUTPUT_KEYS.has(key) && parsed === undefined && value.trim() && !isUrl(value)) {
    return (
      <div className="pr-10 text-sm" data-testid="output-markdown">
        <MarkdownViewer content={value} bodyTextClass="text-foreground" />
      </div>
    )
  }

  if (isUrl(value)) {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer" className="block pr-10 text-sm text-primary hover:underline break-all">{value}</a>
    )
  }

  return <pre className="text-xs font-mono bg-muted p-2 pr-10 rounded overflow-x-auto whitespace-pre-wrap">{value}</pre>
}
